import { takeLatest, put, call } from 'redux-saga/effects';
import axiosInstance from 'clients/api';
import { setError } from '../error/slice';
import {
    HELP_ARTICLES_CALL,
    HELP_ARTICLE_GET_CALL,
    HELP_ARTICLE_CREATE_CALL,
    HELP_ARTICLE_UPDATE_CALL,
    HELP_ARTICLE_DELETE_CALL,
    HELP_CATEGORIES_CALL,
    HELP_CATEGORY_CREATE_CALL,
    HELP_CATEGORY_UPDATE_CALL,
    HELP_CATEGORY_DELETE_CALL,
    HELP_ARTICLES_SEARCH_CALL,
    HELP_ARTICLE_VIEW_CALL,
    HELP_ARTICLE_BY_SLUG_CALL,
    HELP_TENANT_CATEGORIES_CALL,
    HELP_TENANT_ARTICLES_CALL,
    HELP_ARTICLE_UPLOAD_IMAGE_CALL
} from './sagas-actions';
import {
    onCallHelpArticles,
    onSuccessHelpArticles,
    onFailedHelpArticles,
    onCallGetHelpArticle,
    onSuccessGetHelpArticle,
    onFailedGetHelpArticle,
    setSubmitting,
    onSuccessCreateHelpArticle,
    onSuccessUpdateHelpArticle,
    onSuccessDeleteHelpArticle,
    onCallHelpCategories,
    onSuccessHelpCategories,
    onFailedHelpCategories,
    onSuccessCreateHelpCategory,
    onSuccessUpdateHelpCategory,
    onSuccessDeleteHelpCategory,
    onCallSearchHelpArticles,
    onSuccessSearchHelpArticles,
    onFailedSearchHelpArticles,
    onCallGetHelpArticleBySlug,
    onSuccessGetHelpArticleBySlug,
    onFailedGetHelpArticleBySlug,
    onCallTenantArticles,
    onSuccessTenantArticles,
    onFailedTenantArticles
} from './slice';

const adminPrefix = '/super-user/help-article';
const adminCategoryPrefix = '/super-user/help-category';
const tenantPrefix = '/tenantUser/help-article';
const tenantCategoryPrefix = '/tenantUser/help-category';

// Admin article list
export function* helpArticlesCallGenerator(action) {
    yield put(onCallHelpArticles());
    try {
        const params = action.payload || {};
        const { data } = yield call(axiosInstance.get, adminPrefix, { params });
        yield put(onSuccessHelpArticles(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedHelpArticles());
    }
}

// Admin get single article
export function* helpArticleGetCallGenerator(action) {
    yield put(onCallGetHelpArticle());
    try {
        const { articleId } = action.payload;
        const { data } = yield call(axiosInstance.get, `${adminPrefix}/${articleId}`);
        yield put(onSuccessGetHelpArticle(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedGetHelpArticle());
    }
}

// Admin create article
export function* helpArticleCreateCallGenerator(action) {
    yield put(setSubmitting(true));
    try {
        const { data } = yield call(axiosInstance.post, adminPrefix, action.payload);
        yield put(onSuccessCreateHelpArticle(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(setSubmitting(false));
    }
}

// Admin update article
export function* helpArticleUpdateCallGenerator(action) {
    yield put(setSubmitting(true));
    try {
        const { articleId, data: articleData } = action.payload;
        const { data } = yield call(axiosInstance.put, `${adminPrefix}/${articleId}`, articleData);
        yield put(onSuccessUpdateHelpArticle(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(setSubmitting(false));
    }
}

// Admin delete article
export function* helpArticleDeleteCallGenerator(action) {
    yield put(setSubmitting(true));
    try {
        const { articleId } = action.payload;
        yield call(axiosInstance.delete, `${adminPrefix}/${articleId}`);
        yield put(onSuccessDeleteHelpArticle(articleId));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(setSubmitting(false));
    }
}

// Categories list
export function* helpCategoriesCallGenerator(action) {
    yield put(onCallHelpCategories());
    try {
        const { data } = yield call(axiosInstance.get, adminCategoryPrefix);
        yield put(onSuccessHelpCategories(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedHelpCategories());
    }
}

// Category CRUD
export function* helpCategoryCreateCallGenerator(action) {
    yield put(setSubmitting(true));
    try {
        const { data } = yield call(axiosInstance.post, adminCategoryPrefix, action.payload);
        yield put(onSuccessCreateHelpCategory(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(setSubmitting(false));
    }
}

export function* helpCategoryUpdateCallGenerator(action) {
    yield put(setSubmitting(true));
    try {
        const { categoryId, data: categoryData } = action.payload;
        const { data } = yield call(axiosInstance.put, `${adminCategoryPrefix}/${categoryId}`, categoryData);
        yield put(onSuccessUpdateHelpCategory(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(setSubmitting(false));
    }
}

export function* helpCategoryDeleteCallGenerator(action) {
    yield put(setSubmitting(true));
    try {
        const { categoryId } = action.payload;
        yield call(axiosInstance.delete, `${adminCategoryPrefix}/${categoryId}`);
        yield put(onSuccessDeleteHelpCategory(categoryId));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(setSubmitting(false));
    }
}

// End-user search
export function* helpArticlesSearchCallGenerator(action) {
    const { query } = action.payload;
    yield put(onCallSearchHelpArticles(query));
    try {
        const { data } = yield call(axiosInstance.get, `${tenantPrefix}/search`, { params: { q: query } });
        yield put(onSuccessSearchHelpArticles(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedSearchHelpArticles());
    }
}

// End-user record view
export function* helpArticleViewCallGenerator(action) {
    try {
        const { articleId } = action.payload;
        yield call(axiosInstance.post, `${tenantPrefix}/view`, { article_id: articleId });
    } catch (error) {
        // Silent fail for view tracking
    }
}

// End-user get by slug
export function* helpArticleBySlugCallGenerator(action) {
    yield put(onCallGetHelpArticleBySlug());
    try {
        const { slug } = action.payload;
        const { data } = yield call(axiosInstance.get, `${tenantPrefix}/${slug}`);
        yield put(onSuccessGetHelpArticleBySlug(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedGetHelpArticleBySlug());
    }
}

// End-user tenant categories (uses tenant endpoint, not admin)
export function* helpTenantCategoriesCallGenerator(action) {
    yield put(onCallHelpCategories());
    try {
        const { data } = yield call(axiosInstance.get, tenantCategoryPrefix);
        yield put(onSuccessHelpCategories(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedHelpCategories());
    }
}

// End-user tenant articles (with optional category_id filter)
export function* helpTenantArticlesCallGenerator(action) {
    yield put(onCallTenantArticles());
    try {
        const params = action.payload || {};
        const { data } = yield call(axiosInstance.get, tenantPrefix, { params });
        yield put(onSuccessTenantArticles(data));
    } catch (error) {
        yield put(setError({ error, action }));
        yield put(onFailedTenantArticles());
    }
}

// Upload image — accepts FormData (file) or { url } to download from external URL
// POST /super-user/help-article/upload-image
// Stores in app_storage/kb/{uuid}, returns { url: "https://..." }
export function* helpArticleUploadImageCallGenerator(action) {
    try {
        const payload = action.payload;
        let response;

        if (payload instanceof FormData) {
            response = yield call(axiosInstance.post, `${adminPrefix}/upload-image`, payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } else {
            // { url: "https://storage.crisp.chat/..." }
            response = yield call(axiosInstance.post, `${adminPrefix}/upload-image`, payload);
        }

        // Store result on the action for the caller to read
        action._result = response.data;
    } catch (error) {
        yield put(setError({ error, action }));
        action._error = error;
    }
}

export default function* helpArticlesWatcherSaga() {
    yield takeLatest(HELP_ARTICLES_CALL, helpArticlesCallGenerator);
    yield takeLatest(HELP_ARTICLE_GET_CALL, helpArticleGetCallGenerator);
    yield takeLatest(HELP_ARTICLE_CREATE_CALL, helpArticleCreateCallGenerator);
    yield takeLatest(HELP_ARTICLE_UPDATE_CALL, helpArticleUpdateCallGenerator);
    yield takeLatest(HELP_ARTICLE_DELETE_CALL, helpArticleDeleteCallGenerator);
    yield takeLatest(HELP_CATEGORIES_CALL, helpCategoriesCallGenerator);
    yield takeLatest(HELP_CATEGORY_CREATE_CALL, helpCategoryCreateCallGenerator);
    yield takeLatest(HELP_CATEGORY_UPDATE_CALL, helpCategoryUpdateCallGenerator);
    yield takeLatest(HELP_CATEGORY_DELETE_CALL, helpCategoryDeleteCallGenerator);
    yield takeLatest(HELP_ARTICLES_SEARCH_CALL, helpArticlesSearchCallGenerator);
    yield takeLatest(HELP_ARTICLE_VIEW_CALL, helpArticleViewCallGenerator);
    yield takeLatest(HELP_ARTICLE_BY_SLUG_CALL, helpArticleBySlugCallGenerator);
    yield takeLatest(HELP_TENANT_CATEGORIES_CALL, helpTenantCategoriesCallGenerator);
    yield takeLatest(HELP_TENANT_ARTICLES_CALL, helpTenantArticlesCallGenerator);
    yield takeLatest(HELP_ARTICLE_UPLOAD_IMAGE_CALL, helpArticleUploadImageCallGenerator);
}
